import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { dbInsert } from '../../../lib/supabase';

const ProjectEstimator = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({});
  const [estimate, setEstimate] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [leadInfo, setLeadInfo] = useState({ name: '', email: '' });

  const steps = [
    {
      id: 'project-type',
      title: 'Project Type',
      question: 'What type of project do you need?',
      options: [
        { value: 'landing-page', label: 'Landing Page', basePrice: 5000, icon: 'FileText' },
        { value: 'business-website', label: 'Business Website', basePrice: 15000, icon: 'Globe' },
        { value: 'ecommerce', label: 'E-commerce Store', basePrice: 35000, icon: 'ShoppingCart' },
        { value: 'web-app', label: 'Web Application', basePrice: 50000, icon: 'Monitor' },
        { value: 'mobile-app', label: 'Mobile App', basePrice: 75000, icon: 'Smartphone' },
        { value: 'custom-software', label: 'Custom Software', basePrice: 100000, icon: 'Code' }
      ]
    },
    {
      id: 'complexity',
      title: 'Complexity Level',
      question: 'How complex should your project be?',
      options: [
        { value: 'basic', label: 'Basic Features', multiplier: 1, icon: 'Circle' },
        { value: 'standard', label: 'Standard Features', multiplier: 1.5, icon: 'Square' },
        { value: 'advanced', label: 'Advanced Features', multiplier: 2.2, icon: 'Hexagon' },
        { value: 'enterprise', label: 'Enterprise Level', multiplier: 3, icon: 'Star' }
      ]
    },
    {
      id: 'timeline',
      title: 'Timeline',
      question: 'When do you need it completed?',
      options: [
        { value: 'rush', label: 'Rush (1-2 weeks)', multiplier: 1.8, icon: 'Zap' },
        { value: 'fast', label: 'Fast (3-4 weeks)', multiplier: 1.3, icon: 'Clock' },
        { value: 'standard', label: 'Standard (6-8 weeks)', multiplier: 1, icon: 'Calendar' },
        { value: 'flexible', label: 'Flexible (10+ weeks)', multiplier: 0.85, icon: 'Pause' }
      ]
    },
    {
      id: 'features',
      title: 'Additional Features',
      question: 'What additional features do you need?',
      multiple: true,
      options: [
        { value: 'cms', label: 'Content Management', price: 3000, icon: 'Edit' },
        { value: 'seo', label: 'SEO Optimization', price: 2500, icon: 'Search' },
        { value: 'analytics', label: 'Analytics Integration', price: 1500, icon: 'BarChart' },
        { value: 'payment', label: 'Payment Processing', price: 5000, icon: 'CreditCard' },
        { value: 'api', label: 'API Integration', price: 4000, icon: 'Link' },
        { value: 'security', label: 'Advanced Security', price: 6000, icon: 'Shield' }
      ]
    },
    {
      id: 'contact-details',
      title: 'Contact Details',
      question: 'Where should we send your detailed proposal?',
      isContactStep: true
    }
  ];

  const handleSelection = (stepId, value) => {
    setSelections(prev => ({
      ...prev,
      [stepId]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmitInquiry();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmitInquiry = async () => {
    setIsSubmitting(true);
    try {
      // First calculate the estimate to have the data ready
      const projectType = steps[0].options.find(opt => opt.value === selections['project-type']);
      const complexity = steps[1].options.find(opt => opt.value === selections['complexity']);
      const timeline = steps[2].options.find(opt => opt.value === selections['timeline']);
      const features = (selections['features'] || []).map(f => steps[3].options.find(opt => opt.value === f)?.label);

      if (!projectType || !complexity || !timeline) return;

      let basePrice = projectType.basePrice;
      basePrice *= complexity.multiplier;
      basePrice *= timeline.multiplier;

      const featuresPrice = (selections['features'] || []).reduce((total, featureValue) => {
        const feature = steps[3].options.find(opt => opt.value === featureValue);
        return total + (feature ? feature.price : 0);
      }, 0);

      const totalPrice = basePrice + featuresPrice;
      const calculatedEstimate = {
        basePrice: Math.round(basePrice),
        featuresPrice,
        totalPrice: Math.round(totalPrice),
        range: { min: Math.round(totalPrice * 0.8), max: Math.round(totalPrice * 1.2) },
        timeline: getTimelineEstimate(selections['project-type'], selections['complexity'])
      };

      setEstimate(calculatedEstimate);

      // Save to Supabase / Fallback local storage
      await dbInsert('project_inquiries', {
        lead: leadInfo,
        selections: {
          projectType: projectType?.label,
          complexity: complexity?.label,
          timeline: timeline?.label,
          features
        },
        estimate: {
          total: calculatedEstimate.totalPrice,
          range: calculatedEstimate.range,
          completion: calculatedEstimate.timeline
        },
        status: 'new'
      });

      setSubmitSuccess(true);
      setShowResults(true);
    } catch (error) {
      console.error('Error saving inquiry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTimelineEstimate = (projectType, complexity) => {
    const baseWeeks = {
      'landing-page': 2,
      'business-website': 4,
      'ecommerce': 8,
      'web-app': 12,
      'mobile-app': 16,
      'custom-software': 20
    };

    const complexityMultiplier = {
      'basic': 1,
      'standard': 1.3,
      'advanced': 1.8,
      'enterprise': 2.5
    };

    const weeks = Math.round(baseWeeks[projectType] * complexityMultiplier[complexity]);
    return `${weeks} weeks`;
  };

  const resetEstimator = () => {
    setCurrentStep(0);
    setSelections({});
    setEstimate(null);
    setShowResults(false);
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const canProceed = currentStepData.isContactStep
    ? (leadInfo.name.length > 2 && leadInfo.email.includes('@'))
    : selections[currentStepData?.id];

  if (showResults && estimate) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-8 border border-primary/20"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Calculator" size={32} color="white" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-2">Your Project Estimate</h3>
          <p className="text-text-secondary">Based on your requirements, here's what we estimate</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="card p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="IndianRupee" size={20} color="var(--color-primary)" />
              <h4 className="text-lg font-semibold text-foreground">Investment Range</h4>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gradient mb-2">
                ₹{estimate.range.min.toLocaleString()} - ₹{estimate.range.max.toLocaleString()}
              </p>
              <p className="text-sm text-text-secondary">Estimated project cost</p>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Clock" size={20} color="var(--color-accent)" />
              <h4 className="text-lg font-semibold text-foreground">Timeline</h4>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-accent mb-2">{estimate.timeline}</p>
              <p className="text-sm text-text-secondary">Estimated completion</p>
            </div>
          </div>
        </div>

        <div className="card p-6 mb-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">Cost Breakdown</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Base Project Cost</span>
              <span className="font-medium text-foreground">₹{estimate.basePrice.toLocaleString()}</span>
            </div>
            {estimate.featuresPrice > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Additional Features</span>
                <span className="font-medium text-foreground">₹{estimate.featuresPrice.toLocaleString()}</span>
              </div>
            )}
            <div className="border-t border-border pt-3">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-foreground">Total Estimate</span>
                <span className="font-bold text-primary text-lg">₹{estimate.totalPrice.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} color="var(--color-warning)" />
            <div>
              <p className="text-sm text-foreground font-medium mb-1">Important Note</p>
              <p className="text-sm text-text-secondary">
                This is a ballpark estimate. Final pricing depends on detailed requirements,
                design complexity, and specific technical needs. We'll provide an accurate quote after our consultation.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            onClick={resetEstimator}
            iconName="RotateCcw"
            iconPosition="left"
            className="flex-1"
          >
            Start Over
          </Button>
          <Button
            variant="default"
            iconName="MessageCircle"
            iconPosition="right"
            className="flex-1 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Discuss This Estimate
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="card p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-foreground">Project Estimator</h2>
          <div className="text-sm text-text-secondary">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>

        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-gradient-to-r from-primary to-accent h-2 rounded-full transition-all duration-500"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <h4 className="text-xl font-semibold text-foreground mb-2">{currentStepData.title}</h4>
          <p className="text-text-secondary mb-6">{currentStepData.question}</p>

          {currentStepData.isContactStep ? (
            <div className="space-y-4 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Your Name</label>
                  <input
                    type="text"
                    className="w-full bg-muted border border-border rounded-lg p-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none text-foreground"
                    placeholder="John Doe"
                    value={leadInfo.name}
                    onChange={(e) => setLeadInfo(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email Address</label>
                  <input
                    type="email"
                    className="w-full bg-muted border border-border rounded-lg p-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none text-foreground"
                    placeholder="john@company.com"
                    value={leadInfo.email}
                    onChange={(e) => setLeadInfo(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {currentStepData.options.map((option) => {
                const isSelected = currentStepData.multiple
                  ? (selections[currentStepData.id] || []).includes(option.value)
                  : selections[currentStepData.id] === option.value;

                return (
                  <motion.div
                    key={option.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 ${isSelected
                      ? 'border-primary bg-primary/10 shadow-brand'
                      : 'border-border hover:border-primary/50 hover:bg-muted'
                      }`}
                    onClick={() => {
                      if (currentStepData.multiple) {
                        const current = selections[currentStepData.id] || [];
                        const updated = current.includes(option.value)
                          ? current.filter(v => v !== option.value)
                          : [...current, option.value];
                        handleSelection(currentStepData.id, updated);
                      } else {
                        handleSelection(currentStepData.id, option.value);
                      }
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-primary/20' : 'bg-muted'
                        }`}>
                        <Icon
                          name={option.icon}
                          size={20}
                          color={isSelected ? 'var(--color-primary)' : 'var(--color-text-secondary)'}
                        />
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-foreground">{option.label}</h5>
                        {option.basePrice && (
                          <p className="text-sm text-text-secondary">From ₹{option.basePrice.toLocaleString()}</p>
                        )}
                        {option.price && (
                          <p className="text-sm text-text-secondary">+₹{option.price.toLocaleString()}</p>
                        )}
                      </div>
                      {isSelected && (
                        <Icon name="Check" size={20} color="var(--color-primary)" />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 0}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <Button
          variant="default"
          onClick={nextStep}
          disabled={!canProceed}
          iconName={isLastStep ? "Calculator" : "ChevronRight"}
          iconPosition="right"
          className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
        >
          {isLastStep ? 'Calculate Estimate' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export default ProjectEstimator;